import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from '@tanstack/react-table';
import { getMarketOverview, MarketOverviewItem } from '../api/metrics';
import SectionTitle from './shared/SectionTitle';
import StandaloneSection from './shared/StandaloneSection';
import DashboardBottomLogo from './DashboardBottomLogo';
import './MarketOverviewDashboard.css';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';

// Type for filter state
type TrendFilter = 'ALL' | 'UPTREND' | 'DOWNTREND' | 'SIDEWAYS';
type VolatilityFilter = 'ALL' | 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREME';
type MomentumFilter = 'ALL' | 'BULLISH' | 'BEARISH' | 'NEUTRAL';

const MarketOverviewDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Global auto-refresh integration
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('market-overview');

  // Set this dashboard as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('market-overview');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // When global refresh trigger changes, invalidate all queries to refetch data
  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries({ queryKey: ['marketOverview'] });
    }
  }, [refreshTrigger, queryClient]);

  // Filter state
  const [trendFilter, setTrendFilter] = useState<TrendFilter>('ALL');
  const [volatilityFilter, setVolatilityFilter] = useState<VolatilityFilter>('ALL');
  const [momentumFilter, setMomentumFilter] = useState<MomentumFilter>('ALL');

  // Table state
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'priceChangePercentage24h', desc: true }
  ]);

  // Fetch market overview data
  const { data, isLoading, error } = useQuery({
    queryKey: ['marketOverview'],
    queryFn: getMarketOverview,
    staleTime: 30000, // 30 seconds
  });

  // Format price change percentage with color
  const formatPriceChange = (change: number) => {
    const className = change >= 0 ? 'positive' : 'negative';
    const sign = change >= 0 ? '+' : '';
    return <span className={`price-change ${className}`}>{sign}{change.toFixed(2)}%</span>;
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(value);
  };

  // Get trend icon
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'UPTREND':
        return <span className="trend-icon uptrend" title="Uptrend">↑</span>;
      case 'DOWNTREND':
        return <span className="trend-icon downtrend" title="Downtrend">↓</span>;
      case 'SIDEWAYS':
        return <span className="trend-icon sideways" title="Sideways">→</span>;
      default:
        return <span className="trend-icon unknown" title="Unknown">?</span>;
    }
  };

  // Get volatility badge
  const getVolatilityBadge = (state: string) => {
    const className = `volatility-badge volatility-${state.toLowerCase()}`;
    return <span className={className}>{state}</span>;
  };

  // Get momentum badge
  const getMomentumBadge = (direction: string) => {
    const className = `momentum-badge momentum-${direction.toLowerCase()}`;
    return <span className={className}>{direction}</span>;
  };

  // Get pivot status class name
  const getPivotStatusClassName = (status: string): string => {
    if (!status || status === 'No Data') return 'pivot-status pivot-unknown';

    // Resistance levels (above pivot) - red/orange
    if (status.includes('R3') || status.includes('Above R')) return 'pivot-status pivot-above-resistance';
    if (status.includes('R2')) return 'pivot-status pivot-near-resistance';
    if (status.includes('R1')) return 'pivot-status pivot-at-resistance';

    // At pivot point - neutral purple
    if (status.includes('At PP') || status.includes('PP-R1') || status.includes('S1-PP')) return 'pivot-status pivot-at-pivot';

    // Support levels (below pivot) - green/blue
    if (status.includes('S1') && !status.includes('S2') && !status.includes('S3')) return 'pivot-status pivot-at-support';
    if (status.includes('S2')) return 'pivot-status pivot-near-support';
    if (status.includes('S3') || status.includes('Below S')) return 'pivot-status pivot-below-support';

    return 'pivot-status pivot-unknown';
  };

  // Format market regime for display
  const formatMarketRegime = (regime: string) => {
    if (!regime) return 'Unknown';

    // Convert SCREAMING_SNAKE_CASE to Title Case
    return regime
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Define table columns
  const columns = useMemo<ColumnDef<MarketOverviewItem>[]>(
    () => [
      {
        accessorKey: 'productId',
        header: () => <span title="Cryptocurrency trading pair">Product</span>,
        cell: (info) => <span className="product-id">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'currentPrice',
        header: () => <span title="Current market price in USD">Price</span>,
        cell: (info) => <span className="current-price">{formatCurrency(info.getValue() as number)}</span>,
      },
        {
            accessorKey: 'trend',
            header: () => <span title="Current trend direction (uptrend ↑, downtrend ↓, sideways →)">Trend</span>,
            cell: (info) => getTrendIcon(info.getValue() as string),
            enableSorting: false,
        },
        {
            accessorKey: 'trendStrength',
            header: () => <span title="Strength of the current trend (0-100%)">Strength</span>,
            cell: (info) => (
                <div className="strength-bar">
                    <div
                        className="strength-fill"
                        style={{ width: `${(info.getValue() as number) * 100}%` }}
                        title={`Strength: ${((info.getValue() as number) * 100).toFixed(0)}%`}
                    />
                </div>
            ),
            enableSorting: false,
        },
      {
        accessorKey: 'priceChangePercentage24h',
        header: () => <span title="Percentage change in price over the last 24 hours">24h Change</span>,
        cell: (info) => formatPriceChange(info.getValue() as number),
      },
      {
        accessorKey: 'volatilityState',
        header: () => <span title="Current volatility classification (low, medium, high, extreme)">Volatility</span>,
        cell: (info) => getVolatilityBadge(info.getValue() as string),
        enableSorting: false,
      },
      {
        accessorKey: 'volatilityPercentile',
        header: () => <span title="Volatility percentile rank - higher = more volatile than historical average">Vol %ile</span>,
        cell: (info) => <span className="volatility-percentile">{info.getValue() as number}%</span>,
      },
      {
        accessorKey: 'momentumDirection',
        header: () => <span title="Direction of price momentum (bullish, bearish, neutral)">Momentum</span>,
        cell: (info) => getMomentumBadge(info.getValue() as string),
        enableSorting: false,
      },
      {
        accessorKey: 'marketRegime',
        header: () => <span title="Current market condition classification (trending, ranging, reversal potential, etc.)">Regime</span>,
        cell: (info) => <span className="regime">{formatMarketRegime(info.getValue() as string)}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'pivotStatus',
        header: () => <span title="Price position relative to daily pivot points (support/resistance levels)">24H Pivot Status</span>,
        cell: (info) => {
          const status = info.getValue() as string;
          const className = getPivotStatusClassName(status);
          return <span className={className}>{status}</span>;
        },
        enableSorting: false,
      },
        {
            id: 'interpretation',
            header: () => <span title="Market analysis based on technical indicators">Analysis</span>,
            cell: (info) => {
                const item = info.row.original;
                return (
                    <span className={`interpretation interpretation-${item.interpretationSentiment.toLowerCase()}`}>
              {item.interpretation}
            </span>
                );
            },
            enableSorting: false,
        },
      {
        accessorKey: 'volume24h',
        header: () => <span title="Total trading volume over the last 24 hours in USD">Volume 24h</span>,
        cell: (info) => {
          const volume = info.getValue() as number;
          if (volume >= 1000000) {
            return <span className="volume">${(volume / 1000000).toFixed(2)}M</span>;
          } else if (volume >= 1000) {
            return <span className="volume">${(volume / 1000).toFixed(2)}K</span>;
          } else {
            return <span className="volume">${volume.toFixed(2)}</span>;
          }
        },
        enableSorting: false,
      },
      {
        accessorKey: 'relativeVolume',
        header: () => <span title="Current volume relative to average - above 1.0 means higher than normal">Rel. Vol</span>,
        cell: (info) => <span className="relative-volume">{(info.getValue() as number).toFixed(2)}x</span>,
        enableSorting: false,
      },



    ],
    []
  );

  // Filter data based on dropdown selections
  const filteredData = useMemo(() => {
    if (!data?.markets) return [];

    let filtered = data.markets;

    if (trendFilter !== 'ALL') {
      filtered = filtered.filter(m => m.trend === trendFilter);
    }
    if (volatilityFilter !== 'ALL') {
      filtered = filtered.filter(m => m.volatilityState === volatilityFilter);
    }
    if (momentumFilter !== 'ALL') {
      filtered = filtered.filter(m => m.momentumDirection === momentumFilter);
    }

    return filtered;
  }, [data?.markets, trendFilter, volatilityFilter, momentumFilter]);

  // Create table instance
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Clear all filters
  const handleClearFilters = () => {
    setTrendFilter('ALL');
    setVolatilityFilter('ALL');
    setMomentumFilter('ALL');
  };

  return (
    <div className="market-overview-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <SectionTitle title="Market Overview Dashboard" />
      </div>

      {/* Loading State */}
      {isLoading && !data && (
        <div className="dashboard-content">
          <section className="dashboard-section">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading market overview...</p>
            </div>
          </section>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="dashboard-content">
          <section className="dashboard-section">
            <div className="error-state">
              <p className="error-message">
                Failed to load market data: {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </section>
        </div>
      )}

      {/* Market Summary Section - Full Width */}
      {!isLoading && !error && data && (
        <div className="market-summary-full-width">
          <StandaloneSection title="MARKET SUMMARY">
            {/* Filters */}
            <div className="filters-container">
              <div className="filter-group">
                <label>Trend:</label>
                <select value={trendFilter} onChange={(e) => setTrendFilter(e.target.value as TrendFilter)}>
                  <option value="ALL">All</option>
                  <option value="UPTREND">Uptrend</option>
                  <option value="DOWNTREND">Downtrend</option>
                  <option value="SIDEWAYS">Sideways</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Volatility:</label>
                <select value={volatilityFilter} onChange={(e) => setVolatilityFilter(e.target.value as VolatilityFilter)}>
                  <option value="ALL">All</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="EXTREME">Extreme</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Momentum:</label>
                <select value={momentumFilter} onChange={(e) => setMomentumFilter(e.target.value as MomentumFilter)}>
                  <option value="ALL">All</option>
                  <option value="BULLISH">Bullish</option>
                  <option value="BEARISH">Bearish</option>
                  <option value="NEUTRAL">Neutral</option>
                </select>
              </div>

              <button
                onClick={handleClearFilters}
                className="clear-filters-button"
                disabled={trendFilter === 'ALL' && volatilityFilter === 'ALL' && momentumFilter === 'ALL'}
              >
                Clear Filters
              </button>

              <div className="results-count">
                Showing {filteredData.length} of {data?.markets?.length || 0} markets
              </div>
            </div>

            {/* Market Overview Table */}
            <div className="market-overview-table-container">
              <table className="market-overview-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                          className={header.column.getCanSort() ? 'sortable' : ''}
                          style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'desc' ? ' ↓' : ' ↑'
                          ) : ''}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="no-results">
                        No markets match the selected filters
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        className={`market-row quality-${row.original.dataQuality.toLowerCase()} clickable-row`}
                        onClick={() => navigate(`/market/${row.original.productId}`)}
                        style={{ cursor: 'pointer' }}
                        title={`Click to view detailed analysis for ${row.original.productId}`}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer - Click hint */}
            <div className="table-footer-hint">
              💡 <strong>Tip:</strong> Click on any row to view detailed price charts, pivot analysis, and technical indicators for that asset
            </div>
          </StandaloneSection>
        </div>
      )}

      <DashboardBottomLogo />
    </div>
  );
};

export default MarketOverviewDashboard;
